// src/utils/integrations.js
// Inyeccion de scripts de integracion (GTM, GA4) en el <head> del public site.
// Anti-duplicacion: si GTM esta activo, GA4 NO se inyecta (GTM carga GA4 via tag).
// Anti-XSS: usa textContent (nunca innerHTML). Solo acepta IDs validados por el backend.
//
// El backend (tenantIntegrationsController endpoint publico) valida con regex:
// - google_tag_manager: GTM-XXXXXXX (7-9 chars alfanumericos en mayusculas)
// - google_analytics_4:  G-XXXXXXXXXX (exactamente 10 chars alfanumericos en mayusculas)
//
// Defense-in-depth: el frontend VALIDA OTRA VEZ con regex antes de inyectar.

'use strict';

const GTM_REGEX = /^GTM-[A-Z0-9]{7,9}$/;
const GA4_REGEX = /^G-[A-Z0-9]{10}$/;
const API_BASE = (typeof window !== 'undefined' && window.__API_BASE__) || '';

/**
 * Quita scripts de integracion previamente inyectados (usado en navegacion SPA).
 */
function removeInjectedIntegrations() {
    if (typeof document === 'undefined') return;
    const scripts = document.querySelectorAll('script[data-tenant-integration]');
    scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
}

/**
 * Inyecta el snippet de GTM en el <head>.
 * Usa textContent (no innerHTML) para prevenir XSS.
 */
function injectGtm(gtmId) {
    if (!gtmId || !GTM_REGEX.test(gtmId)) {
        // Defense-in-depth: si el ID no pasa la regex, NO inyectamos
        return false;
    }
    if (typeof document === 'undefined') return false;

    // GTM head snippet
    const head = document.createElement('script');
    head.setAttribute('data-tenant-integration', 'gtm');
    head.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
    document.head.appendChild(head);

    // GTM noscript fallback (para users con JS deshabilitado)
    const noscript = document.createElement('noscript');
    noscript.setAttribute('data-tenant-integration', 'gtm-noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    return true;
}

/**
 * Inyecta el snippet de GA4 en el <head>.
 * SOLO se debe llamar si NO hay GTM activo (anti-duplicacion).
 */
function injectGa4(ga4Id) {
    if (!ga4Id || !GA4_REGEX.test(ga4Id)) {
        return false;
    }
    if (typeof document === 'undefined') return false;

    // gtag.js loader
    const loader = document.createElement('script');
    loader.setAttribute('data-tenant-integration', 'ga4-loader');
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
    document.head.appendChild(loader);

    // gtag config init
    const init = document.createElement('script');
    init.setAttribute('data-tenant-integration', 'ga4-init');
    init.textContent = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${ga4Id}');`;
    document.head.appendChild(init);

    return true;
}

/**
 * Carga las integraciones del tenant y las inyecta en el <head>.
 * @param {number} tenantId - el ID del tenant actual
 * @returns {Promise<{gtm: boolean, ga4: boolean}>}
 */
export async function injectIntegrations(tenantId) {
    if (!tenantId || !Number.isInteger(tenantId) || tenantId <= 0) {
        return { gtm: false, ga4: false, error: 'invalid tenantId' };
    }
    if (typeof document === 'undefined') {
        return { gtm: false, ga4: false, error: 'no document' };
    }

    // Quitar integraciones previas (para SPA navigation)
    removeInjectedIntegrations();

    try {
        const res = await fetch(`${API_BASE}/api/tenant/integrations/public?tenantId=${tenantId}`, {
            method: 'GET',
            headers: { 'Accept': 'application/json' },
            credentials: 'omit'
        });

        if (!res.ok) {
            return { gtm: false, ga4: false, error: `HTTP ${res.status}` };
        }

        const json = await res.json();
        const data = json.data || json;
        const gtm = data.gtm || null;
        const ga4 = data.ga4 || null;

        const result = { gtm: false, ga4: false };

        // Prioridad: GTM. Si GTM esta activo, GA4 NO se inyecta
        // (GA4 deberia cargarse via GTM tag, no via script directo)
        if (gtm) {
            const ok = injectGtm(gtm);
            result.gtm = ok;
        } else if (ga4) {
            const ok = injectGa4(ga4);
            result.ga4 = ok;
        }

        return result;
    } catch (err) {
        // No romper la UX si falla el fetch
        console.warn('[integrations] fetch failed:', err);
        return { gtm: false, ga4: false, error: err.message };
    }
}

/**
 * Version sync para tests.
 */
export function _testHelpers() {
    return {
        GTM_REGEX,
        GA4_REGEX,
        injectGtm,
        injectGa4,
        removeInjectedIntegrations
    };
}
